import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import {
  BETA_ACCESS_DAYS,
  BETA_ACCESS_NOTE,
  BETA_AUDIENCE,
  BETA_CONSENT_TEXT,
  BETA_HEADLINE,
  BETA_INTRO,
  BETA_SOURCE_OPTIONS
} from "../constants/betaSignup.js";
import { getBetaUtmParams, resolveInitialBetaSource, trackBetaEvent } from "../utils/betaTracking.js";
import { validateBetaSignupForm } from "../utils/betaSignupValidation.js";

const BetaSignup = () => {
  const honeypotRef = useRef(null);
  const errorRef = useRef(null);
  const submittingRef = useRef(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("");
  const [sourceOther, setSourceOther] = useState("");
  const [reflectingOn, setReflectingOn] = useState("");
  const [willingToFeedback, setWillingToFeedback] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const initial = resolveInitialBetaSource();
    if (initial) setSource(initial);
    trackBetaEvent("beta_page_view", getBetaUtmParams());
  }, []);

  const showError = (message) => {
    setStatus({ type: "error", message });
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || submitting) return;

    const validationErrors = validateBetaSignupForm({
      fullName,
      email,
      source,
      sourceOther,
      reflectingOn,
      willingToFeedback,
      consent
    });
    if (validationErrors.length) {
      showError(validationErrors.join(" "));
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setStatus({ type: "", message: "" });
    trackBetaEvent("beta_form_submit_attempt", { source: source || "unknown" });

    try {
      const utm = getBetaUtmParams();
      const res = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/submitBetaSignup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          honeypot: honeypotRef.current?.value || "",
          fullName: fullName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          source,
          sourceOther,
          reflectingOn: reflectingOn.trim(),
          willingToFeedback: true,
          consent: true,
          ...utm
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      trackBetaEvent("beta_form_submit_success", {
        source: source || "unknown",
        campaign: data.campaignTag || ""
      });
      setSubmitted(true);
      setStatus({
        type: "success",
        message: data.alreadySignedUp
          ? "You are already signed up for beta access. Log in with the same email to continue testing."
          : `Thank you for joining the beta. You have ${BETA_ACCESS_DAYS} days of full access once you log in with the email you used here.`
      });
    } catch (err) {
      trackBetaEvent("beta_form_submit_error", { message: err?.message || "unknown" });
      showError(err?.message || "Submission failed.");
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageContainer className="podcast-feature-page beta-signup-page" size="wide">
        <h1 className="podcast-feature-page__title">You are in</h1>
        <p className="podcast-feature-page__lead">{status.message}</p>
        <p className="podcast-feature-page__lead">
          Log in with the same email to start reflecting. Your {BETA_ACCESS_DAYS}-day beta access is active from signup.
        </p>
        <Link className="primary-button beta-signup-page__cta" to="/login">
          Go to login
        </Link>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="podcast-feature-page beta-signup-page" size="wide">
      <h1 className="podcast-feature-page__title">{BETA_HEADLINE}</h1>
      <p className="podcast-feature-page__lead">{BETA_INTRO}</p>
      <p className="podcast-feature-page__lead">{BETA_AUDIENCE}</p>
      <p className="podcast-feature-page__lead beta-signup-page__access-note">{BETA_ACCESS_NOTE}</p>

      <form
        className="podcast-feature-form podcast-feature-page__panel"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
      >
        <div className="podcast-feature-form__hp" aria-hidden="true">
          <input
            ref={honeypotRef}
            id="beta-honeypot"
            name="b_website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            defaultValue=""
          />
        </div>

        <div
          ref={errorRef}
          className={`beta-signup-page__status${status.message ? " beta-signup-page__status--visible" : ""}`}
        >
          {status.message ? (
            <p
              className={
                status.type === "error"
                  ? "podcast-feature-form__alert podcast-feature-form__alert--error"
                  : "podcast-feature-form__alert podcast-feature-form__alert--ok"
              }
              role="alert"
            >
              {status.message}
            </p>
          ) : null}
        </div>

        <div className={`beta-signup-page__fields${submitting ? " beta-signup-page__fields--busy" : ""}`}>
          <div className="podcast-feature-form__field">
            <label htmlFor="beta-full-name">Full name *</label>
            <input
              id="beta-full-name"
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(ev) => setFullName(ev.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="podcast-feature-form__field">
            <label htmlFor="beta-email">Email *</label>
            <input
              id="beta-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="podcast-feature-form__field">
            <label htmlFor="beta-phone">Phone (optional)</label>
            <input
              id="beta-phone"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(ev) => setPhone(ev.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="podcast-feature-form__field">
            <label htmlFor="beta-source">Where did you hear about Ahavibes? *</label>
            <select
              id="beta-source"
              value={source}
              onChange={(ev) => setSource(ev.target.value)}
              disabled={submitting}
              required
            >
              <option value="">Select one</option>
              {BETA_SOURCE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {source === "Other" ? (
            <div className="podcast-feature-form__field">
              <label htmlFor="beta-source-other">Please specify *</label>
              <input
                id="beta-source-other"
                type="text"
                value={sourceOther}
                onChange={(ev) => setSourceOther(ev.target.value)}
                disabled={submitting}
                required
              />
            </div>
          ) : null}

          <div className="podcast-feature-form__field">
            <label htmlFor="beta-reflecting">What do you want help reflecting on? *</label>
            <textarea
              id="beta-reflecting"
              rows={4}
              value={reflectingOn}
              onChange={(ev) => setReflectingOn(ev.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <fieldset className="podcast-feature-form__fieldset" disabled={submitting}>
            <legend>Willing to test and give feedback? *</legend>
            <label className="podcast-feature-form__radio">
              <input
                type="radio"
                name="beta-feedback"
                value="yes"
                checked={willingToFeedback === "yes"}
                onChange={() => {
                  setWillingToFeedback("yes");
                  if (status.type === "error") setStatus({ type: "", message: "" });
                }}
              />
              Yes
            </label>
            <label className="podcast-feature-form__radio">
              <input
                type="radio"
                name="beta-feedback"
                value="no"
                checked={willingToFeedback === "no"}
                onChange={() => {
                  setWillingToFeedback("no");
                  showError("Beta testing requires willingness to share feedback — choose Yes.");
                }}
              />
              No
            </label>
          </fieldset>

          <label className="podcast-feature-form__radio beta-signup-page__consent">
            <input
              type="checkbox"
              checked={consent}
              onChange={(ev) => setConsent(ev.target.checked)}
              disabled={submitting}
            />
            {BETA_CONSENT_TEXT}
          </label>

          <button
            type="submit"
            className="primary-button beta-signup-page__submit"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? "Submitting…" : "Join the beta"}
          </button>
        </div>
      </form>
    </PageContainer>
  );
};

export default BetaSignup;
